import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Award, BadgeCheck, ExternalLink, X } from "lucide-react";
import { awardsMedia, awardSources } from "@/lib/site-data";

type AwardsFeatureProps = { variant?: "home" | "city" };

export function AwardsFeature({ variant = "home" }: AwardsFeatureProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    if (!isExpanded) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setIsExpanded(false);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [isExpanded]);

  return (
    <>
      <section
        className={`awards-feature section-pad${variant === "city" ? " awards-feature-city" : ""}`}
        aria-labelledby={`awards-title-${variant}`}
      >
        <div className="shell-wide awards-feature-grid">
          <figure className="awards-feature-photo">
            <button
              className="awards-feature-preview"
              type="button"
              onClick={() => setIsExpanded(true)}
              aria-label="Powiększ zdjęcie wyróżnień"
            >
              <img
                src={awardsMedia.src}
                width={awardsMedia.width}
                height={awardsMedia.height}
                loading="lazy"
                decoding="async"
                alt={awardsMedia.alt}
              />
            </button>
            <figcaption>Docenieni za jakość i zaufanie</figcaption>
          </figure>
          <div className="awards-feature-copy">
            <span className="section-kicker">wyróżnienia</span>
            <h2 id={`awards-title-${variant}`}>
              Docenieni za jakość i zaufanie
            </h2>
            <p>
              Orły Prawa 2024 (Katowice) oraz Certyfikat Firma Godna Zaufania
              2026.
            </p>
            <div
              className="awards-feature-list"
              aria-label="Widoczne wyróżnienia"
            >
              <p>
                <Award size={21} />
                <span>
                  <strong>Orły Prawa 2024</strong>
                  <br />
                  Laureat konkursu w Katowicach.
                  <br />
                  <a
                    className="award-source-link"
                    href={awardSources.orlyPrawa}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Źródło wyróżnienia <ExternalLink size={14} />
                  </a>
                </span>
              </p>
              <p>
                <BadgeCheck size={21} />
                <span>
                  <strong>Firma Godna Zaufania 2026</strong>
                  <br />
                  Certyfikat widoczny na przekazanym zdjęciu.
                  <br />
                  <a
                    className="award-source-link"
                    href={awardSources.firmaGodnaZaufania}
                    target="_blank"
                    rel="noreferrer"
                  >
                    Źródło certyfikatu <ExternalLink size={14} />
                  </a>
                </span>
              </p>
            </div>
          </div>
        </div>
      </section>
      {isExpanded &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className="awards-lightbox"
            role="dialog"
            aria-modal="true"
            aria-label="Powiększone zdjęcie wyróżnień"
            onClick={() => setIsExpanded(false)}
          >
            <div
              className="awards-lightbox-content"
              onClick={event => event.stopPropagation()}
            >
              <button
                className="awards-lightbox-close"
                type="button"
                onClick={() => setIsExpanded(false)}
                aria-label="Zamknij powiększenie"
              >
                <X size={22} />
              </button>
              <img
                src={awardsMedia.src}
                width={awardsMedia.width}
                height={awardsMedia.height}
                alt={awardsMedia.alt}
              />
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
